import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { nanoid } from 'nanoid';
import React, { memo, useCallback, useMemo } from 'react';

import { Link } from '@sravni/react-design-system/lib/Typography';
import { useIsMobile } from '@sravni/react-utils';
import { open } from '@sravni/vitrina/lib/helpers/navigationHelper';
import { replaceTemplate } from '@sravni/vitrina/lib/helpers/replaceHelper';
import type IOrganizationInfoContent from '@sravni/vitrina/lib/types/config/IOrganizationInfoContent';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';

import { useProductConfig } from '../../../../../../store/hooks/productPopupInfo';
import FormTypes from '../../enums/formTypes';
import { usePixel } from '../../hooks/pixel';
import { usePaymentNetwork } from '../../hooks/usePaymentNetwork';
import { sendAdvertisingButtonClickEvent } from '../../utils/analytics/events';

import InfoDepositBlock from './InfoDepositBlock';
import style from './style.module.scss';

const DASH_OF_DEFAULT_VALUE = '⸺';

interface IProps {
  className?: string;
  classNameRow?: string;
  contentClassName?: string;
  product: IProduct;
  isPopap?: boolean;
  isAccordion?: boolean;
  showHead?: boolean;
  formType?: FormTypes;
}

const OrganizationInfo: React.FC<IProps> = memo((props: IProps) => {
  const { current: sub } = React.useRef(nanoid());
  const isMobile = useIsMobile();
  const { className, classNameRow, product, contentClassName, isPopap, isAccordion, showHead = true, formType } = props;
  const {
    eventCategory,
    list: { desktop, mobile },
    isAdvertisingCommon,
  } = useProductConfig();
  const { advertising, organization, link } = product;
  const config = isMobile ? mobile : desktop;
  const {
    productInfo: { organizationInfo },
  } = config;
  const { paymentNetwork } = usePaymentNetwork(product);

  const organizationHead = useMemo((): string => {
    if (!showHead) {
      return '';
    }

    const { heading } = organizationInfo;
    const { template, properties } = heading;

    return replaceTemplate(template, properties, product);
  }, [organizationInfo, product, showHead]);

  const href = usePixel(advertising?.monetization?.pixels?.click, {
    productLink: isAdvertisingCommon ? link : undefined,
    product,
    sub,
    paymentNetwork,
  });

  const handleClick = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      event.stopPropagation();

      sendAdvertisingButtonClickEvent(
        {
          advertising,
          product,
        },
        eventCategory,
        paymentNetwork,
      );
      open({ href, target: '_blank' });
    },
    [advertising, product, eventCategory, paymentNetwork, href],
  );

  if (!organizationInfo) {
    return null;
  }

  const renderContent = (content: IOrganizationInfoContent | IOrganizationInfoContent[]): React.ReactNode => {
    if (Array.isArray(content)) {
      const values = content.map((items) => {
        const { template, properties } = items;
        const replaceValue = replaceTemplate(template, properties, product, DASH_OF_DEFAULT_VALUE);
        return {
          ...items,
          replaceValue,
        };
      });

      if (values.every((item) => item.replaceValue === DASH_OF_DEFAULT_VALUE)) {
        return null;
      }

      return (
        <div className={style.wrapper}>
          {values.map((items, index) => {
            const { replaceValue, title } = items;
            return replaceValue ? (
              // eslint-disable-next-line react/no-array-index-key
              <div className={style['wrapper-item']} key={index}>
                {title ? <div className={style['wrapper-item-title']}>{title}</div> : null}
                <div className={cn(style['wrapper-item-content'], { [style.isPopap]: isPopap })}>{replaceValue}</div>
              </div>
            ) : null;
          })}
        </div>
      );
    }

    const { template, properties } = content;

    return replaceTemplate(template, properties, product);
  };

  const renderInfoBlock = (): React.ReactNode => {
    const { contents } = organizationInfo;

    return contents.map(({ title, content }) => {
      const contentResult = renderContent(content);
      return contentResult ? (
        <div className={cn(classNameRow, style.sections, { [style.accordion]: isAccordion })}>
          <div className={style.title}>{title}</div>
          <div className={cn(style.content, { [style.isPopap]: isPopap })}>{contentResult}</div>
        </div>
      ) : null;
    });
  };

  const renderWebSite = (): React.ReactNode => {
    if (formType === FormTypes.Landing) {
      return null;
    }

    const url =
      typeof organization.contacts?.website === 'object'
        ? organization.contacts.website.url
        : organization.contacts?.website;
    return (
      organization.contacts?.website && (
        <div className={style.sections}>
          <div className={style.title}>Сайт</div>
          <div className={cn(style.content, { [style.isPopap]: isPopap })}>
            {isEmpty(advertising) ? (
              <div>{url}</div>
            ) : (
              <Link href={href} onClick={handleClick}>
                {url}
              </Link>
            )}
          </div>
        </div>
      )
    );
  };

  return (
    <div className={cn(className)}>
      {showHead && <div className={style.subTitle}>{organizationHead}</div>}
      <div className={cn(style.tabs, contentClassName)}>
        {renderInfoBlock()}
        {renderWebSite()}
      </div>
      <InfoDepositBlock organizationName={organization.name.full} />
    </div>
  );
});

export default OrganizationInfo;
